import type * as TooltipPrimitive from '@radix-ui/react-tooltip'
import type { ComponentProps, ReactNode } from 'react'

import Info from '../../icons/Info'
import { Tooltip, TooltipContent, TooltipTrigger } from '../_base/Tooltip'

interface InfoTooltipProps extends ComponentProps<typeof TooltipPrimitive.Root> {
  content: string | React.ReactNode
  title?: string
  trigger?: ReactNode
  showInfoIcon?: boolean
  infoIconSize?: number
  tooltipContentProps?: React.ComponentProps<typeof TooltipPrimitive.Content>
}

/**
 * InfoTooltip displays an info icon with a tooltip, without unwanted button styling.
 * Use to provide contextual information with a clean, minimal trigger.
 */
export function InfoTooltip({
  content,
  title,
  trigger,
  showInfoIcon,
  infoIconSize = 14,
  tooltipContentProps,
  ...tooltipProps
}: InfoTooltipProps) {
  const shouldShowInfoIcon = showInfoIcon ?? !trigger

  const tooltipTrigger = shouldShowInfoIcon ? (
    <span
      className="group focus-visible:ring-ring/50 flex w-fit cursor-help items-center gap-1 rounded-sm outline-none focus-visible:ring-[3px]"
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.code === 'Space') {
          e.preventDefault()
          e.currentTarget.click()
        }
      }}
      aria-label={trigger ? undefined : 'More info'}
    >
      {trigger}
      <Info
        width={infoIconSize}
        height={infoIconSize}
        className="text-muted-foreground group-hover:text-foreground group-focus-visible:text-foreground transition-colors"
      />
    </span>
  ) : (
    trigger
  )

  if (!tooltipTrigger) {
    return null
  }

  return (
    <Tooltip {...tooltipProps}>
      <TooltipTrigger asChild>{tooltipTrigger}</TooltipTrigger>
      <TooltipContent title={title} {...tooltipContentProps}>
        {content}
      </TooltipContent>
    </Tooltip>
  )
}
